import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { createEmolument } from "../services/payment";
import { listFrequencies } from "../services/teacher";
import WizardSteps from "./shared/WizardSteps";
import { levelKey } from "../utils/classesLabels";

const TOTAL_STEPS = 2;

const initialState = {
  taxName: "",
  taxValue: "",
  courseId: "",
};

// Split out of the former TaxasLayer. Same "hide cadastro from the menu"
// philosophy applied to ListaProdutosLayer/CadastrarProdutoLayer — this
// screen is only reachable via the "Adicionar Taxa" button on
// ListaTaxasLayer, not from the sidebar.
const CadastrarTaxaLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [frequencies, setFrequencies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const stepLabels = [t("taxa.wizard.step.data"), t("taxa.wizard.step.confirm")];

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, []);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const nextStep = () => {
    if (!form.taxName || !form.taxValue) {
      toast.error(t("taxa.error.requiredFields"));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await createEmolument({
        taxName: form.taxName,
        taxValue: Number(form.taxValue),
        courseId: form.courseId || undefined,
        periodicity: "Apenas uma vez",
        recorrency: "",
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || t("taxa.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("taxa.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("taxa.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("taxa.field.name")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='f7:person' />
                        </span>
                        <input type='text' className='form-control' value={form.taxName} onChange={setField("taxName")} required />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("taxa.field.value")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:percent-square-outline' />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.taxValue} onChange={setField("taxValue")} required />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t(levelKey("taxa.field.course"))}</label>
                      <select className='form-select' value={form.courseId} onChange={setField("courseId")}>
                        <option value=''>{t(levelKey("taxa.field.course.allLevels"))}</option>
                        {frequencies.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.description}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("taxa.confirm.description")}</div>
                  <ul className='list-unstyled d-flex flex-column gap-8'>
                    <li>
                      <strong>{t("taxa.field.name")}:</strong> {form.taxName}
                    </li>
                    <li>
                      <strong>{t("taxa.field.value")}:</strong> {form.taxValue}
                    </li>
                    <li>
                      <strong>{t(levelKey("taxa.field.course"))}:</strong> {form.courseId ? frequencies.find((f) => f.id === form.courseId)?.description : t(levelKey("taxa.field.course.allLevels"))}
                    </li>
                  </ul>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={prevStep}
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-submit btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
                      {submitting ? (
                        t("global.loading")
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("taxa.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("taxa.action.createAnother")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 px-32'
                      onClick={() => navigate("/pagamentos/taxas")}
                    >
                      <i className='ri-arrow-left-line' /> {t("taxa.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarTaxaLayer;
